'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { ApiError } from '@/lib/api-client';
import { login } from '@/services/auth';
import { normalizeRole } from '@/utils/roles';

type LoginStatus = 'idle' | 'success' | 'error';

export default function LoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<LoginStatus>('idle');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      console.info('[login] submitting', {
        apiUrl: '/api/login',
        usuario: username,
      });

      const payload = await login({
        usuario: username,
        password,
      });

      console.info('[login] success payload', payload);
      
      // Fail-safe: Save user role and name to localStorage
      if (typeof window !== 'undefined') {
        const rawRole = payload?.usuario?.roles?.[0]?.codigo || 'user';
        const userRole = normalizeRole(rawRole);
        const userName = payload?.usuario?.nombre_usuario || '';
        const authToken =
          payload?.token ||
          (payload as { access_token?: string })?.access_token ||
          (payload as { data?: { token?: string; access_token?: string } })?.data?.token ||
          (payload as { data?: { token?: string; access_token?: string } })?.data?.access_token ||
          '';
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('userName', userName);
        if (authToken) {
          localStorage.setItem('authToken', authToken);
        }
      }

      setStatus('success');
      setMessage('Redirigiendo...');
      router.push('/dashboard');
    } catch (error) {
      const fallbackMessage = 'No se pudo conectar con el servidor.';

      if (error instanceof ApiError) {
        console.warn('[login] api error', {
          status: error.status,
          code: error.code,
          requestId: error.requestId,
          path: error.path,
        });
        setMessage(error.message || fallbackMessage);
      } else {
        console.error('[login] request failed', error);
        setMessage(fallbackMessage);
      }

      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen lg:h-screen lg:overflow-hidden bg-white font-[family-name:var(--font-libre-franklin)] text-[#004A91]">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between bg-[#002D5C] p-8 lg:p-12 text-white border-l-[8px] border-[#FAED6C] relative overflow-hidden">
        
        {/* Animated Background Bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 animate-[pulse_4s_ease-in-out_infinite]"></div>
          <div className="absolute top-[20%] left-[10%] w-32 h-32 rounded-full bg-white/5 animate-[pulse_5s_ease-in-out_infinite_1s]"></div>
          <div className="absolute bottom-[30%] right-[10%] w-48 h-48 rounded-full bg-[#FAED6C]/5 animate-[pulse_6s_ease-in-out_infinite_2s]"></div>
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#5594F1]/10 animate-[pulse_7s_ease-in-out_infinite_1.5s]"></div>
        </div>

        {/* Top Logo */}
        <div className="relative h-56 w-full max-w-[500px] mb-2 z-10">
           <Image 
             src="/logos/muni-lujan-blanco.webp" 
             alt="Luján Logo" 
             fill
             sizes="(min-width: 1024px) 500px, 80vw"
             className="object-contain object-left" 
             priority
           />
        </div>

        {/* Center Text */}
        <div className="flex relative mt-auto mb-auto z-10">
           {/* Yellow line */}
           <div className="absolute left-0 top-1 bottom-1 w-1.5 bg-[#FAED6C] rounded-full"></div>
           <div className="pl-6 md:pl-8">
             <p className="text-[#FAED6C] text-xs md:text-sm font-bold tracking-widest mb-3 uppercase font-[family-name:var(--font-libre-franklin)]">Municipio de Luján</p>
             <h1 className="text-3xl md:text-5xl lg:text-6xl font-[family-name:var(--font-righteous)] leading-[1.1] mb-6 tracking-wider">
               Sistema de<br/>
               Indicadores<br/>
               SICIS
             </h1>
             <div className="text-sm lg:text-base text-blue-100 space-y-1 font-light opacity-90 font-[family-name:var(--font-libre-franklin)]">
               <p>Ciudad Inteligente y Sostenible</p>
               <p>Alianza Universitaria Argentina - Europea</p>
             </div>
           </div>
        </div>

        {/* Bottom Logos */}
        <div className="mt-4 z-10">
          <p className="text-xs text-blue-200 mb-4 font-light font-[family-name:var(--font-libre-franklin)]">En colaboración con</p>
          <div className="flex flex-col gap-4">
             {/* CIN & UE Logo - No white box, using blanco version */}
             <div className="w-64 h-20 relative overflow-hidden">
                 <Image
                   src="/logos/logos-ue-uniuear-cin-blanco.webp"
                   alt="CIN / UE"
                   fill
                   sizes="(min-width: 1024px) 256px, 60vw"
                   className="object-contain object-left"
                 />
              </div>
             
             {/* UNLu and UNSAdA with white background boxes */}
             <div className="w-48 h-12 relative bg-white rounded-sm overflow-hidden p-1 shadow-sm">
                 <Image
                   src="/logos/unlu-texto-1.webp"
                   alt="UNLu"
                   fill
                   sizes="192px"
                   className="object-contain"
                 />
              </div>
              <div className="w-48 h-12 relative bg-white rounded-sm overflow-hidden p-1 shadow-sm">
                 <Image
                   src="/logos/logo-unsada.webp"
                   alt="UNSAdA"
                   fill
                   sizes="192px"
                   className="object-contain"
                 />
              </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex w-full lg:w-[55%] flex-col relative items-center justify-center bg-white p-6 sm:p-12">
         {/* Main Card */}
         <div className="w-full max-w-[420px] rounded-2xl bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border-t-[3px] border-[#5594F1]">
            <h2 className="text-2xl font-bold text-[#004A91] font-[family-name:var(--font-righteous)] tracking-wide mb-1">Acceso al Sistema</h2>
            <p className="text-gray-400 text-sm mb-8 font-light font-[family-name:var(--font-libre-franklin)]">Ingresá tus credenciales para continuar</p>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {message && (
                <div
                  className={`rounded-lg border px-4 py-3 text-sm font-[family-name:var(--font-libre-franklin)] ${
                    status === 'success'
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {message}
                </div>
              )}

              <div>
                <label htmlFor="username" className="mb-2 block text-xs font-bold text-[#004A91]">Usuario</label>
                <input 
                  type="text" 
                  id="username"
                  className="w-full rounded-lg bg-[#EEEEEE] border border-transparent focus:bg-white focus:border-[#004A91] focus:ring-2 focus:ring-[#004A91]/20 px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 text-gray-700 font-[family-name:var(--font-libre-franklin)]" 
                  placeholder="usuario@municipio.gob.ar" 
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-xs font-bold text-[#004A91]">Contraseña</label>
                <input 
                  type="password" 
                  id="password"
                  className="w-full rounded-lg bg-[#EEEEEE] border border-transparent focus:bg-white focus:border-[#004A91] focus:ring-2 focus:ring-[#004A91]/20 px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 text-gray-700 font-[family-name:var(--font-libre-franklin)]" 
                  placeholder="............" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="flex justify-start pt-1 pb-2">
                 <button type="button" className="text-sm text-[#5594F1] hover:text-[#004A91] transition-colors font-[family-name:var(--font-libre-franklin)]">¿Olvidaste tu contraseña?</button>
              </div>


              <button 
                type="submit" 
                className="w-full rounded-lg bg-[#004A91] py-3.5 text-sm font-bold tracking-wide text-white transition-all hover:bg-[#003A75] hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 font-[family-name:var(--font-libre-franklin)]"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'INGRESANDO...' : 'INGRESAR AL SISTEMA'}
              </button>

              <div className="mt-8 flex items-center justify-center text-xs text-gray-400 gap-1.5 font-[family-name:var(--font-libre-franklin)]">
                 <span>🔒</span> Conexión segura · SICIS v1.0
              </div>
            </form>
         </div>

         {/* Footer */}
         <div className="absolute bottom-6 w-full text-center px-4 max-w-2xl font-[family-name:var(--font-libre-franklin)]">
           <div className="border-t border-gray-100 pt-4 text-[10px] sm:text-xs text-gray-400 font-light">
             © {new Date().getFullYear()} · Municipio de Luján · UNLu · UNSAdA · CIN · Unión Europea
           </div>
         </div>
      </div>
    </div>
  );
}
